import { action, makeObservable, observable, runInAction } from 'mobx';

import {
  AvailabilityStatus,
  AvailabilityStatusService,
  BackgroundCheck,
  BackgroundCheckService,
  DataResponse,
  JobAvailabilityType,
  Language,
  Sitter,
  SitterService,
  SitterSpecialSituation,
  SitterSpecialSituationsService,
  SitterTeachingSkill,
  SitterTeachingSkillsService,
  SpecialExperience,
  SpecialService,
  SpecialSituation,
  TeachingSkill,
  WillSit,
} from 'us-web-services';
import { UserStore } from '../UserStore';
import { Storage } from '../../constants';

export class SitterStore {
  #isSitter: boolean;
  #sitterId?: number;
  #userId: number;
  sitter: Sitter;
  specialSituations: SitterSpecialSituation[];
  teachingSkills: SitterTeachingSkill[];
  backgroundCheck?: BackgroundCheck;

  constructor(user: UserStore) {
    this.#isSitter = user.user?.sitter || false;
    this.#userId = user.user?.id || 0;
    this.#sitterId = this.#isSitter ? (user.user?.id || 0) : undefined;
    const localSitter = localStorage.getItem(Storage.US_SITTER_OBJECT);

    this.sitter = localSitter ? JSON.parse(localSitter) as Sitter : {
      address: {
        city: '',
        neighborhood: '',
        state: '',
        zip: '',
      }
    };

    this.specialSituations = [];
    this.teachingSkills = [];

    makeObservable(this, {
      availabilityHasSelection: observable,
      availabilityIsSelected: observable,
      availabilitySave: action,
      availabilityToggle: action,
      backgroundCheck: observable,
      getBackgroundCheck: action,
      getSpecialSituations: action,
      getTeachingSkills: action,
      languageAdd: action,
      languageRemove: action,
      specialExperienceAdd: action,
      specialExperienceRemove: action,
      specialServiceAdd: action,
      specialServiceRemove: action,
      specialSituationAdd: action,
      specialSituationRemove: action,
      teachingSkillAdd: action,
      teachingSkillRemove: action,
      verticalAdd: action,
      verticalRemove: action,
      refreshSitter: action,
      sitter: observable,
      updateSitter: action,
    });
  };

  refreshSitter = async (): Promise<void> => {
    const config = {
      params: {
        userId: this.#userId,
        sitterId: this.#userId,
      }
    }
    const response = (await SitterService.get(this.#userId, config)).data;

    runInAction(() => {
      this.sitter = response.data;
      localStorage.setItem(Storage.US_SITTER_OBJECT, JSON.stringify(this.sitter));
      if (this.sitter && !this.sitter.jobPreferences?.length) {
        this.sitter.jobPreferences = [
          { type: JobAvailabilityType.occasional },
          { type: JobAvailabilityType.partTime },
          { type: JobAvailabilityType.fullTime },
        ];
      }
    });
  };

  getBackgroundCheck = async (): Promise<void> => {
    const response = (await BackgroundCheckService.findByFilter({
      params: {
        sitterId: this.#userId,
      },
    })).data;

    if (response.data) {
      [this.backgroundCheck] = response.data;
    }
  };

  getSpecialSituations = async (): Promise<SitterSpecialSituation[]> => {
    const response = (await SitterSpecialSituationsService.getByFilter({
      params: {
        sitterId: this.#userId,
      },
    })).data;

    this.specialSituations = response.data;

    return this.specialSituations;
  };

  getTeachingSkills = async (): Promise<SitterTeachingSkill[]> => {
    const response = (await SitterTeachingSkillsService.getByFilter({
      params: {
        sitterId: this.#userId,
      },
    })).data;

    this.teachingSkills = response.data;

    return this.teachingSkills;
  };

  availabilityHasSelection = (): boolean => !!this.sitter.jobPreferences && this.sitter.jobPreferences.length > 0;

  availabilityIsSelected = (type: JobAvailabilityType): boolean =>
    !!this.sitter.jobPreferences?.find(x => x.type === type);

  availabilitySave = async (): Promise<DataResponse<AvailabilityStatus>> => {
    const params = {
      id: this.sitter.availabilityStatus?.id,
      jobPreferences: this.sitter.jobPreferences,
      sitterId: this.#sitterId,
    };

    if (this.sitter.availabilityStatus?.id) {
      return AvailabilityStatusService.patch(this.sitter.availabilityStatus.id, params);
    }

    return AvailabilityStatusService.create(params);
  };

  availabilityToggle = (type: JobAvailabilityType, active: boolean): void => {
    if (active) {
      this.sitter.jobPreferences?.push({ type });
    } else if (this.sitter.jobPreferences) {
      this.sitter.jobPreferences = this.sitter.jobPreferences.filter(x => x.type !== type);
    }
  };

  languageAdd = async (language: Language): Promise<void> => {
    const updatedLanguages = this.sitter.languages || [];

    updatedLanguages.push(language);

    const sitterPatch = {
      id: this.#userId,
      languages: updatedLanguages,
    }

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  languageRemove = async (language: Language): Promise<void> => {
    const updatedLanguages = this.sitter.languages ? this.sitter.languages.filter(x => x.name !== language.name) : [];

    const sitterPatch = {
      id: this.#userId,
      languages: updatedLanguages,
    }

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  specialExperienceAdd = async (experience: SpecialExperience): Promise<void> => {
    const updatedExperiences = this.sitter.specialExperiences || [];

    updatedExperiences.push(experience);

    const sitterPatch = {
      id: this.#userId,
      specialExperiences: updatedExperiences,
    }

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  specialExperienceRemove = async (experience: SpecialExperience): Promise<void> => {
    const updatedExperiences = this.sitter.specialExperiences ? this.sitter.specialExperiences.filter(x => x.name !== experience.name) : [];

    const sitterPatch = {
      id: this.#userId,
      specialExperiences: updatedExperiences,
    }

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  specialServiceAdd = async (service: SpecialService): Promise<void> => {
    const updatedServices = this.sitter.specialServices || [];

    updatedServices.push(service);

    const sitterPatch = {
      id: this.#userId,
      specialServices: updatedServices,
    }

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  specialServiceRemove = async (service: SpecialService): Promise<void> => {
    const updatedServices = this.sitter.specialServices ? this.sitter.specialServices.filter(x => x.name !== service.name) : [];

    const sitterPatch = {
      id: this.#userId,
      specialServices: updatedServices,
    }

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  specialSituationAdd = async (situation: SpecialSituation): Promise<void> => {
    const sitterSituation = {
      sitterId: this.#userId,
      specialSituation: situation,
    };

    await SitterSpecialSituationsService.create(sitterSituation);
    await this.getSpecialSituations();
    await this.refreshSitter();
  };

  specialSituationRemove = async (situation: SpecialSituation): Promise<void> => {
    const targetSituation = this.specialSituations.find(s => s.specialSituation.name === situation.name);

    if (targetSituation?.id) {
      await SitterSpecialSituationsService.delete(targetSituation.id);
    }

    await this.getSpecialSituations();
    await this.refreshSitter();
  };

  teachingSkillAdd = async (teachingSkill: TeachingSkill): Promise<void> => {
    const sitterSkill = {
      sitterId: this.#userId,
      teachingSkill,
    }

    await SitterTeachingSkillsService.create(sitterSkill);
    await this.getTeachingSkills();
    await this.refreshSitter();
  };

  teachingSkillRemove = async (skill: TeachingSkill): Promise<void> => {
    const targetSkill = this.teachingSkills.find(s => s.teachingSkill.name === skill.name);

    if (targetSkill?.id) {
      await SitterTeachingSkillsService.delete(targetSkill.id);
    }

    await this.getTeachingSkills();
    await this.refreshSitter();
  };

  verticalAdd = async (vertical: WillSit): Promise<void> => {
    const sitterPatch: Sitter = {
        id: this.#userId,
    };

    sitterPatch[`willSit${vertical}`] = true;

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  verticalRemove = async (vertical: WillSit): Promise<void> => {
    const sitterPatch: Sitter = {
        id: this.#userId,
    };

    sitterPatch[`willSit${vertical}`] = false;

    const patchedSitter = (await SitterService.patch(this.#userId, sitterPatch)).data;

    this.updateSitter(patchedSitter.data);
  };

  updateSitter(sitter: Sitter): void {
    if (sitter && sitter.id) {
      this.sitter = sitter;
      localStorage.setItem(Storage.US_SITTER_OBJECT, JSON.stringify(this.sitter));
    } else {
      localStorage.removeItem(Storage.US_SITTER_OBJECT);
    }
  };
}
